import { Link } from "react-router-dom";
import { ArrowRight, Heart, Building2, School, HandHeart, Mail } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Reveal";
import { useCashApp } from "@/components/CashAppModal";

const FunderSlot = ({ title, desc }) => (
  <Reveal className="h-full">
    <div
      className="h-full rounded-2xl border-2 border-dashed border-purple-200 bg-lavender-50 p-8 min-h-[200px] flex flex-col justify-center"
      data-testid="funder-slot"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-700">Reserved space</p>
      <h3 className="font-display text-xl font-bold text-slate-900 mt-2.5">{title}</h3>
      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{desc}</p>
    </div>
  </Reveal>
);

const CATEGORIES = [
  {
    icon: Building2,
    title: "Corporate & Foundation Partners",
    copy: "Funding, technology, professional expertise, and exposure that expand what Y.E.S. can offer young people.",
  },
  {
    icon: School,
    title: "School & Community Partners",
    copy: "Space, educational resources, programming, and access to new opportunities — alongside districts, municipalities, educators, and youth-serving organizations.",
  },
  {
    icon: HandHeart,
    title: "Program Sponsors",
    copy: "Transportation, meals, supplies, and enrichment experiences that make individual programs possible for every young person who wants in.",
  },
];

const Partner = () => {
  const { open } = useCashApp();
  return (
    <main className="pt-32 pb-24">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6" data-testid="partner-hero">
        <SectionHead
          eyebrow="Partner With Y.E.S."
          title="Let's build what our youth deserve."
          sub="Creating lasting opportunities for young people takes more than one organization. Y.E.S. partners with schools, businesses, foundations, community organizations, municipalities, educators, and other youth-serving organizations to expand what's possible for young people."
        />
        <Reveal delay={0.1} className="flex flex-wrap gap-3 mt-9">
          <Link to="/#contact" data-testid="partner-contact-btn" className="btn-primary px-7 py-3.5">
            Become a Partner <ArrowRight className="w-4 h-4" />
          </Link>
          <button onClick={open} data-testid="partner-give-btn" className="btn-outline px-7 py-3.5">
            <Heart className="w-4 h-4" /> Invest in Our Youth
          </button>
        </Reveal>
      </section>

      {/* The Need */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="partner-need">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead
              eyebrow="The need"
              title="The talent is already here. The access isn't always."
            />
            <Reveal delay={0.1}>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-5">
                In every neighborhood, there are young people with talent, ideas, leadership
                potential, and a future bigger than their circumstances. What varies is access — to
                mentors, programs, experiences, and adults who open doors.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mt-4">
                Y.E.S. exists to close that gap — and partners are how it happens.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <blockquote className="rounded-3xl bg-gradient-to-br from-lavender-100 to-fuchsia-50 border border-purple-200 p-10 shadow-lg shadow-purple-100">
              <p className="font-display text-xl sm:text-2xl font-bold text-purple-950 leading-snug">
                &ldquo;Y.E.S. isn&apos;t here to decide who a young person should become. We&apos;re
                here to make sure they have the support, exposure, resources, and opportunities to
                discover it.&rdquo;
              </p>
              <footer className="font-mono text-[11px] uppercase tracking-[0.25em] text-purple-700 mt-6">
                The Y.E.S. belief
              </footer>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-purple-100" data-testid="partner-categories">
        <SectionHead
          eyebrow="Partnership"
          title="There's a place for you in this work"
          sub="Whether a partner can provide funding, space, professional expertise, educational resources, transportation, technology, meals, programming, exposure, or access to new opportunities — there's a place for them in this work."
        />
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {CATEGORIES.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.06} className="h-full">
              <div className="h-full rounded-2xl bg-white border border-purple-100 p-8 flex flex-col shadow-sm hover:shadow-xl hover:shadow-purple-200/50 transition-shadow">
                <div className="w-11 h-11 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center shrink-0">
                  <o.icon className="w-5 h-5 text-purple-700" />
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mt-5">{o.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mt-2 flex-1">{o.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="font-display text-2xl sm:text-3xl font-extrabold text-center mt-14">
            Don&apos;t just sponsor an event. <span className="text-shine">Help us build something that lasts.</span>
          </p>
        </Reveal>
      </section>

      {/* Reserved funder spaces */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-purple-100" data-testid="partner-funder-slots">
        <SectionHead
          eyebrow="For funders"
          title="Documentation, on the way"
          sub="These spaces are reserved for verified information — added only as it's confirmed and approved for publication."
        />
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          <FunderSlot
            title="Verified Outcomes"
            desc="Program outcomes and results will be posted here once verified and approved."
          />
          <FunderSlot
            title="Financial Information"
            desc="Financial summaries and fund allocation details will be shared here as they're prepared."
          />
          <FunderSlot
            title="Partner Names"
            desc="School districts, organizations, and supporters we work with will be listed here with their permission."
          />
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 pt-8" data-testid="partner-cta">
        <Reveal>
          <div className="rounded-3xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 p-10 sm:p-14 text-center relative overflow-hidden shadow-2xl shadow-purple-300/50">
            <div className="absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-white/15 blur-[100px] pointer-events-none" />
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Let&apos;s build what our youth deserve.
            </h2>
            <p className="text-purple-100 mt-4 max-w-xl mx-auto leading-relaxed">
              One conversation is all it takes to start. Reach out and let&apos;s figure out what we
              can do together.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-9">
              <Link to="/#contact" data-testid="partner-cta-contact-btn" className="btn bg-white text-purple-800 hover:bg-purple-50 px-8 py-3.5">
                <Mail className="w-4 h-4" /> Become a Partner
              </Link>
              <button onClick={open} data-testid="partner-cta-give-btn" className="btn border border-white/70 text-white hover:bg-white/10 px-8 py-3.5">
                <Heart className="w-4 h-4" /> Invest in Our Youth
              </button>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Partner;